import React from 'react';
import { gql, useQuery } from '@apollo/client';
import { Message, Icon, Label } from 'semantic-ui-react';

/**
 * Explains why a course SKU is not acceptable, as it is being built.
 *
 * ---------------------------------------------------------------------------------------
 * WHY THIS ASKS THE SERVER INSTEAD OF CHECKING LOCALLY
 * ---------------------------------------------------------------------------------------
 *
 * The curriculum rules live in one place — `backend/src/graphql/utils/skuValidation.ts` — and
 * the mutation enforces them. A copy of those rules here would be a second implementation of
 * the same spec, and the moment Social & Recreation's rules arrive the two would disagree: the
 * form would accept a SKU the mutation then rejects, which is the most confusing possible
 * outcome for somebody filling in a form.
 *
 * So the form asks the same function that will judge it.
 */

export const VALIDATE_SKU = gql`
  query ValidateCourseSku($sku: String!) {
    validateCourseSku(sku: $sku) {
      sku
      valid
      issues { index segment value severity message suggestions }
    }
  }
`;

export const VALIDATE_SKUS = gql`
  query ValidateCourseSkus($skus: [String!]!) {
    validateCourseSkus(skus: $skus) {
      sku
      valid
      issues { index segment value severity message suggestions }
    }
  }
`;

export interface SkuIssue {
    index: number | null;
    segment: string;
    value: string;
    severity: 'ERROR' | 'WARNING';
    message: string;
    suggestions: string[];
}

export interface SkuValidation {
    sku: string;
    valid: boolean;
    issues: SkuIssue[];
}

/** The issue list on its own, for callers that already have a validation result. */
export const SkuIssueList: React.FC<{ validation: SkuValidation }> = ({ validation }) => {
    const errors = validation.issues.filter((i) => i.severity === 'ERROR');
    const warnings = validation.issues.filter((i) => i.severity === 'WARNING');

    if (errors.length === 0 && warnings.length === 0) {
        return (
            <Message positive size="tiny">
                <Icon name="check circle" />
                <code>{validation.sku}</code> looks right.
            </Message>
        );
    }

    return (
        <>
            {errors.length > 0 && (
                <Message error size="tiny">
                    <Message.Header>This SKU cannot be used</Message.Header>
                    <Message.List>
                        {errors.map((i) => (
                            <Message.Item key={`${i.index}-${i.value}-${i.message}`}>
                                {/* The segment name saves counting dashes to find the part
                                    being complained about. */}
                                {i.index != null && (
                                    <Label size="mini" color="red" basic style={{ marginRight: '0.5em' }}>
                                        part {i.index + 1}: {i.segment}
                                    </Label>
                                )}
                                {i.message}
                                {i.suggestions.length > 0 && (
                                    <div style={{ marginTop: '2px' }}>
                                        Accepted here:{' '}
                                        {i.suggestions.map((sug) => (
                                            <Label key={sug} size="mini" style={{ marginRight: '3px' }}>{sug}</Label>
                                        ))}
                                    </div>
                                )}
                            </Message.Item>
                        ))}
                    </Message.List>
                </Message>
            )}

            {/* Warnings do not block saving. A stream with no rules yet is unchecked, not
                wrong, and saying so is more honest than silence. */}
            {warnings.map((i) => (
                <Message warning size="tiny" key={i.message}>
                    <Icon name="info circle" />
                    {i.message}
                </Message>
            ))}
        </>
    );
};

/** Validates one SKU against the server and explains the result. */
export const SkuFeedback: React.FC<{ sku: string }> = ({ sku }) => {
    const trimmed = (sku ?? '').trim();

    // A half-built SKU is not a mistake to be told off for — the picker starts every part as
    // "XX" and fills them in one at a time. Stay quiet until there is something to judge.
    const incomplete = trimmed.length === 0 || trimmed.split('-').some((p) => p === 'XX' || p === '');

    const { data } = useQuery(VALIDATE_SKU, {
        variables: { sku: trimmed },
        skip: incomplete,
        fetchPolicy: 'cache-first',
    });

    if (incomplete) {
        return null;
    }

    if (data?.validateCourseSku == null) {
        return null;
    }

    return <SkuIssueList validation={data.validateCourseSku} />;
};
